import type { FormEventHandler } from 'react'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { forgotPassword, login, sendOTPVerificationCode } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import * as Analytics from 'analytics/analytics'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import HelpPane from 'components/Common/HelpPane'
import ContactContext from 'contexts/contactContext'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'

const Or = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${rem(12)};

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
    display: block;
  }
`

interface LoginForm {
  email: string
  password: string
}

interface Props {
  error?: any
  processing: boolean
  user: App.User
  isStoreMode: boolean
  onModeChange?: (mode: App.UiAccountModalMode) => void
  otpEnabled?: boolean
  dismissable?: boolean
}

function AccountAccessLoginPassword(props: Props) {
  const {
    error,
    processing,
    user,
    isStoreMode,
    otpEnabled,
    onModeChange = noop,
    dismissable,
  } = props

  const intl = useIntl()
  const [sendingCode, setSendingCode] = useState<boolean>(false)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const isMobile = useScreenSizeOnly('mobile')

  const currentContact = useContext(ContactContext)
  const contactNumber = currentContact.defaultContact

  const currentPathName = useAppSelector(state => state.router.location.pathname)
  const currentSearch = useAppSelector(state => state.router.location.search)
  const callbackPath = currentPathName + currentSearch

  useEffect(() => {
    if (sendingCode && !processing && !error) {
      onModeChange('loginOTP')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendingCode, processing, error])

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  useEffect(() => {
    const loginType = user.email ? 'email' : 'phone'
    if (user.maskedPhone) {
      Analytics.trackClientEvent({
        subject: 'sign_in_password_or_otp',
        action: 'impression',
        category: loginType,
        type: 'operational',
      })
    } else {
      Analytics.trackClientEvent({
        subject: 'sign_in_password',
        action: 'impression',
        category: loginType,
        type: 'operational',
      })
    }

    if (!isMobile || !user.maskedPhone) {
      nativeInputRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dispatch = useAppDispatch()

  const reRef = useRef<ReCAPTCHA>(null)

  const onForgotPassword = useCallback(() => {
    if (user.email) {
      // forgot password only works with an email, not phone number (for now?)
      dispatch(forgotPassword(user.email, callbackPath))
      onModeChange('forgotPassword')
    }
  }, [dispatch, onModeChange, user, callbackPath])

  const onLogin = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    const loginData = formToObject<LoginForm>(event.currentTarget)
    const recaptchaData = await reRef.current?.executeAsync()
    if (recaptchaData) {
      dispatch(login(user, loginData.password, recaptchaData))
    }
  }, [dispatch, user])

  const onSendVerificationCode = useCallback(() => {
    dispatch(sendOTPVerificationCode(user))
    setSendingCode(true)
  }, [dispatch, user])

  const onBack = useCallback(() => onModeChange('login'), [onModeChange])

  const welcomeBackTitle = intl.formatMessage({
    id: 'GCK7i6Lo/m',
    defaultMessage: 'Welcome back',
    description: 'Modal title for the login password form',
  })

  const serverErrorText = intl.formatMessage({
    id: 'UbwTVSOrmt',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Generic server error message on login password form',
  })

  const passwordLabel = intl.formatMessage({
    id: 'IrS5E8vXs1',
    defaultMessage: 'Password',
    description: 'Label for password input on login form',
  })

  const passwordPlaceholder = intl.formatMessage({
    id: 'xouqea3+lK',
    defaultMessage: 'Password',
    description: 'Placeholder for password input on login form',
  })

  const passwordRequired = intl.formatMessage({
    id: 'Ccp9X8/A1V',
    defaultMessage: 'Password is required',
    description: 'Validation error when password is empty on login',
  })

  const loginWithPasswordLabel = intl.formatMessage({
    id: 'fh6/OdnO5r',
    defaultMessage: 'Log in with password',
    description: 'Button text to log in with password when phone OTP is also available',
  })

  const loginLabel = intl.formatMessage({
    id: 'a/iEJhR0Pl',
    defaultMessage: 'Log in',
    description: 'Button text to log in with password',
  })

  const forgotPasswordLabel = intl.formatMessage({
    id: 'N5DZh5Kzf6',
    defaultMessage: 'Forgot your password?',
    description: 'Link text to initiate password reset on login form',
  })

  const loginWithEmailLabel = intl.formatMessage({
    id: 'Z0m1f1f7ds',
    defaultMessage: 'Log in with email',
    description: 'Link text to switch to email login when logging in with phone',
  })

  const orLabel = intl.formatMessage({
    id: 'ap5sfJYsVZ',
    defaultMessage: 'or',
    description: 'Divider text between password login and OTP login options',
  })

  const sendVerificationCodeLabel = intl.formatMessage({
    id: 'LiU3RpkeJG',
    defaultMessage: 'Send me a verification code',
    description: 'Button text to request OTP verification code via SMS',
  })

  // smsSentToText uses FormattedMessage inline (rich text with <strong>)

  return (<>
    <ModalHeader
      title={welcomeBackTitle}
      onBackButtonClick={onBack}
      dismissible={dismissable}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24} as="form" onSubmit={onLogin} name="loginForm">
          {error?.status === 500 && <MessageBanner kind="critical">{serverErrorText}</MessageBanner>}
          {error?.message && error.status !== 500 && <MessageBanner kind="critical">{error.message}</MessageBanner>}
          <VerticalSpacer gap={16}>
            <fieldset>
              <HiddenInput
                id="email"
                name="email"
                value={user.email}
              />
              <PasswordInput
                label={passwordLabel}
                id="password"
                required
                name="password"
                placeholder={passwordPlaceholder}
                requiredErrorMessage={passwordRequired}
                ref={nativeInputRef}
              />
              <Group direction="vertical" gap={16} horizontalAlign="center">
                <TextButton
                  kind="primary"
                  size="large"
                  type="submit"
                  fit="flex"
                >
                  {user.maskedPhone ? loginWithPasswordLabel : loginLabel}
                </TextButton>
                {!isStoreMode && <>
                  {user.email && <TextLink
                    onClick={onForgotPassword}
                    size="medium"
                  >
                    {forgotPasswordLabel}
                  </TextLink>}
                  {!user.email && <TextLink
                    onClick={() => onModeChange('login', '')}
                    size="medium"
                  >
                    {loginWithEmailLabel}
                  </TextLink>}
                </>}
                {otpEnabled && user.maskedPhone && (
                  <Group direction="vertical" gap={16} horizontalAlign="center" fullWidth>
                    <Or>
                      <BodyText colour="neutral-two" variant="medium">{orLabel}</BodyText>
                    </Or>
                    <TextButton
                      kind="secondary"
                      size="large"
                      fit="flex"
                      onClick={onSendVerificationCode}
                    >
                      {sendVerificationCodeLabel}
                    </TextButton>
                    <BodyText variant="medium">
                      <FormattedMessage
                        id="0Zo9p1fQQ2"
                        defaultMessage="SMS will be sent to <strong>{maskedPhone}</strong>"
                        description="Help text showing where the OTP SMS will be sent"
                        values={{
                          maskedPhone: user.maskedPhone,
                          strong: (chunks) => <strong>{chunks}</strong>,
                        }}
                      />
                    </BodyText>
                  </Group>
                )}
              </Group>
              {error?.status === 500 && (
                <HelpPane contactNumber={contactNumber} />
              )}
            </fieldset>
            {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
          </VerticalSpacer>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    processing: state.auth.processing,
    isStoreMode: state.system.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessLoginPassword)
